<script setup lang="ts">
import {
    ModelValueEmitsType,
    ModelValuePropsInterface,
    useModelValueComposable,
} from '@components/form/compsables/model-value-composable.js';
import InputLabel from '@src/application/input-label.vue';
import { computed } from 'vue';
import FormError from '@components/form/input-base-components/form-error.vue';
import { BordersProps, getBordersComposable } from '@components/form/compsables/borders-composable.js';
import { DisabledPropsInterface, useDisableComposable } from '@src/application/composables/disabled-composable.js';
import {
    FormPartEmitsType,
    FormPartProps,
    useFormPartComposable,
} from '@src/form/composables/form-part-composable.js';
import { isObject } from 'lodash';

interface Option {
    label?: string,
    value?: any,
    [key: string]: any,
}

const props = defineProps<DisabledPropsInterface & ModelValuePropsInterface<Option|any|null> & FormPartProps & BordersProps & {
    options: Array<Option|any>,
    label?: string|false,
    noLabelTopGap?: boolean,
    placeholder?: string,
    noSort?: boolean,
    labelProperty?: string
    valueProperty?: string
    identifierProperty?: string
    large?: boolean,
}>();

defineEmits<ModelValueEmitsType<Option|any|null> & FormPartEmitsType>();

const { modelValue, exposes: modelValueExposes } = useModelValueComposable<Option|any|null>();
const { validationMessage, exposes: formPartExposes } = useFormPartComposable(modelValue);
defineExpose({ ...formPartExposes, ...modelValueExposes });

const preparedOptions = computed<Array<Option|any>>(() => {
    const options = [...props.options];
    for (const option of options) {
        const index = options.indexOf(option);
        if (typeof option === 'object') {
            continue;
        }

        options[index] = {
            [props.labelProperty ?? 'label']: option,
            [props.valueProperty ?? 'value']: option,
        };
    }
    if (!props.noSort) {
        (options as Array<Option>).sort((a: Option, b: Option) => {
            return a[props.labelProperty ?? 'label']
                .toString()
                .localeCompare(b[props.labelProperty ?? 'label'].toString());
        });
    }

    return options;
});

function onSelect(event: Event) {
    if (typeof props.options[0] === 'object') {
        let selectedOption: Option|any|null|undefined = findSelectedOption(props.valueProperty ?? 'value', event);
        if (!selectedOption) {
            selectedOption = findSelectedOption(props.labelProperty ?? 'label', event);
        }
        if (!selectedOption && props.identifierProperty) {
            selectedOption = findSelectedOption(props.identifierProperty, event);
        }
        if (!selectedOption) {
            selectedOption = null;
        }

        if (selectedOption && props.valueProperty) {
            modelValue.value = selectedOption[props.valueProperty];
            return;
        }

        if (selectedOption && 'value' in selectedOption) {
            modelValue.value = selectedOption.value;
            return;
        }

        modelValue.value = selectedOption;
        return;
    }

    modelValue.value = (event.target as HTMLSelectElement).value === '' ? null : (event.target as HTMLSelectElement).value;
}

function findSelectedOption(property: string, event: Event) {
    for (const option of props.options) {
        if (!isObject(option)) {
            continue;
        }

        // @ts-ignore
        if (option[property] === undefined) {
            continue;
        }

        // @ts-ignore
        if (option[property].toString() !== (event.target as HTMLSelectElement).value) {
            continue;
        }

        return option;
    }
}

const { bordersConfig, focusBordersConfig } = getBordersComposable();

const { disabled } = useDisableComposable();

function isSelected(option: Option) {
    if (modelValue.value === null) {
        return false;
    }

    if (props.valueProperty) {
        return option[props.valueProperty] === modelValue.value;
    }

    if (
        props.identifierProperty &&
        typeof modelValue.value === 'object' &&
        props.identifierProperty in option &&
        props.identifierProperty in modelValue.value
    ) {
        return option[props.identifierProperty] === modelValue.value[props.identifierProperty];
    }

    if (option.value === modelValue.value) {
        return true;
    }

    return JSON.stringify(modelValue.value) === JSON.stringify(option);
}

function getOptionValue(option: Option) {
    if (props.identifierProperty) {
        return option[props.identifierProperty];
    }

    if (!props.valueProperty) {
        return option[props.labelProperty ?? 'label'];
    }

    if (option[props.valueProperty ?? 'value'] === null) {
        return '';
    }

    return option[props.valueProperty ?? 'value'];
}
</script>

<template>
    <div :class="[$style.select, large ? $style.large : '', validationMessage !== null ? $style.invalid : '']">
        <InputLabel v-if="label" :label="label" :no-label-top-gap="noLabelTopGap"/>
        <div :class="$style.select__wrapper">
            <select
                :class="[
                    $style.select_input,
                    bordersConfig.top ? $style.border_top : '',
                    bordersConfig.right ? $style.border_right : '',
                    bordersConfig.bottom ? $style.border_bottom : '',
                    bordersConfig.left ? $style.border_left : '',
                    focusBordersConfig.top ? $style.border_top_focus : '',
                    focusBordersConfig.right ? $style.border_right_focus : '',
                    focusBordersConfig.bottom ? $style.border_bottom_focus : '',
                    focusBordersConfig.left ? $style.border_left_focus : '',
                ]"
                :disabled="disabled"
                @change="onSelect"
            >
                <option v-if="!required || required && (modelValue === null || modelValue === undefined)" value="" :selected="modelValue === null">{{ placeholder ?? 'Bitte auswählen' }}</option>
                <option v-for="(option) in preparedOptions" :key="JSON.stringify(option[valueProperty ?? 'value'])" :value="getOptionValue(option)" :selected="isSelected(option)">{{ option[labelProperty ?? 'label'] }}</option>
            </select>
        </div>
        <FormError v-if="validationMessage" :message="validationMessage"/>
    </div>
</template>

<style lang="scss" module>
@use '@variables' as *;
@use '@borders' as *;
@use '@sizes' as *;

.select {
    width: $input-width;
    min-width: $input-min-width;
    max-width: 100%;
    height: inherit;
}
.select__wrapper {
    position: relative;
    height: 100%;
    // select benötigt umschließendes div, damit der nachfolgende custom-gestylte arrow mittig ist und das label nicht mit einschließt

    &::before {
        position: absolute;
        top: 50%;
        right: 1em;
        content: '';
        border-top: .4em solid #52677a;
        border-right: .4em solid transparent;
        border-left: .4em solid transparent;
        transform: translate(0, -.2em);
    }
}
.select_input {
    width: $input-width;
    height: 100%;
    min-height: $input-min-height;
    padding: $input-padding-vertical 2.5em $input-padding-vertical .5em;
    font-size: $font-size-0;
    line-height: 1.3;
    color: $input-font-color;
    cursor: pointer;
    background-color: transparent;
    border: none;
    border-radius: $input-border-radius;

    &:disabled {
        cursor: no-drop;
        background-color: $input-background-color-disabled;
        border: $input-border-hidden;
    }

    &:focus {
        outline: none;

        &.border_top_focus {
            border-top: $input-border-focus
        }
        &.border_right_focus {
            border-right: $input-border-focus
        }
        &.border_bottom_focus {
            border-bottom: $input-border-focus
        }
        &.border_left_focus {
            border-left: $input-border-focus
        }
    }
    &:not(:disabled) {
        &.border_top {
            border-top: $input-border;
        }
        &.border_right {
            border-right: $input-border;
        }
        &.border_bottom {
            border-bottom: $input-border;
        }
        &.border_left {
            border-left: $input-border;
        }
    }
}
.large {
    .select__wrapper::before {
        right: 1.5em;
    }
    .select_input {
        padding-top: calc( #{$input-large-padding} * .95);
        padding-bottom: $input-large-padding;
        padding-left: calc(#{$input-large-padding} * .75);
        font-size: $font-size-1;
        min-height: $input-large-min-height;

        option {
            font-size: 1rem;
        }
    }
}
.invalid .select_input {
    border-color: $red-line-color;
}
</style>
